import React, {useState, useEffect, useMemo, useCallback, useRef} from 'react';
import 'date-fns';
import styled, {useTheme} from 'styled-components';
import {Link} from 'react-router-dom';
import Layout from './Layout';
import LayoutHeader, {LayoutHeaderIconTypes} from 'components/LayoutHeader';
import NavCrumb from '../components/Navigation/NavCrumb';
import StoresWorldMap from '../components/Map/StoresWorldMap';
import Select from '@material-ui/core/Select';
import Button from '@material-ui/core/Button';
import Switch from '@material-ui/core/Switch';
import Modal from '@material-ui/core/Modal';
import Backdrop from '@material-ui/core/Backdrop';
import Fade from '@material-ui/core/Fade';
import Icon from '@mdi/react';
import {mdiDownloadOutline} from '@mdi/js';
import {addJsonSheet, createWorkbook, downloadWorkbook} from 'utils/excelExport';
import SGTitle from 'components/Typography/SGTitle';
import SearchStoreHealth from 'components/Search/SearchStoreHealth';
import SGHeadline from 'components/Typography/SGHeadline';
import Container from 'components/Container';
import Grid from '@material-ui/core/Grid';
import Paper from 'components/Paper';
import SGBody from 'components/Typography/SGBody';
import {useSelector, useDispatch} from 'react-redux';
import Chart from 'react-apexcharts';
import Table from '../components/Tables/TableStructure/Table';
import TableText from '../components/Tables/TableStructure/TableText';
import {rdxRetailDeviceData, rdxRetailDeviceDataPending} from 'redux/reducers/retailStoreReducer';
import {useOktaAuth} from '@okta/okta-react';
import getRetailStoresData from 'redux/actions/getRetailStoresData';
import {fetchHistoryStoreData} from 'redux/actions';
import Skeleton from '@material-ui/lab/Skeleton';
import DateFnsUtils from '@date-io/date-fns';
import {MuiPickersUtilsProvider, KeyboardDatePicker} from '@material-ui/pickers';
import DateRangeIcon from '@material-ui/icons/DateRange';
import config from 'oktaConfig';
import {useLocation} from 'react-router-dom';
import useRetailHardwareStatusLogs from 'hooks/useRetailHardwareStatusLogs';
import {getThemedSelectMenuProps} from 'utils/muiMenuProps';
import InsightsPickersThemeProvider from 'components/MUI/InsightsPickersThemeProvider';
import ThemedSelectMenuItem from 'components/MUI/ThemedSelectMenuItem';

const StyledLayout = styled(Layout)`
  .header-root {
    margin-bottom: 24px;
  }
  @media (min-width: 600px) {
    .header-root {
      margin-bottom: 24px;
    }
  }

  /* Make breadcrumb text larger on this page */
  .storehealth-breadcrumb a {
    font-size: 0.8rem;
  }
`;

const StyledApexLineChart = styled(Paper)`
  .apexcharts-tooltip {
    background: ${props => props.theme.tableRow};
    border: 1px solid ${props => props.theme.text};
    color: ${props => props.theme.text};

    .apexcharts-tooltip-title {
      background: ${props => props.theme.tableRow};
    }
  }

  .apexcharts-xaxistooltip {
    background: ${props => props.theme.tableRow};
    border: 1px solid ${props => props.theme.text};
    color: ${props => props.theme.text};
  }

  .apexcharts-xaxis-label,
  .apexcharts-yaxis-label {
    fill: ${props => props.theme.text};
  }

  .apexcharts-datalabel {
    fill: ${props => props.theme.text};
  }

  .apexcharts-xaxis-texts-g text {
    cursor: pointer;
    &:hover {
      fill: ${props => props.theme.activeStateBlue};
    }
  }

  height: calc(100% - 48px);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding-left: 24px;
  padding-right: 24px;
`;

const ExportButton = styled(Button)`
  background-color: ${props => props.theme.exportButtonBg || props.theme.activeStateBlue};
  text-transform: capitalize;
  font-family: 'Trade Gothic Bold Condensed';
  border-radius: 10px;
  padding: 8px 16px;
  line-height: 18px;
  color: ${props => props.theme.defaultNavy};
  font-size: 0.875rem;
  margin-left: 1rem;
  &:hover {
    background-color: ${props => `${props.theme.exportButtonBg || props.theme.activeStateBlue}cc`};
  }
  &.MuiButton-root.Mui-disabled {
    background-color: ${props => props.theme.grey}80;
    color: ${props => props.theme.defaultNavy};
  }
`;

const TitleSection = styled.div`
  display: flex;
  margin-bottom: 0.5rem;
  justify-content: space-between;
`;

const HeaderSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.5rem;
  width: 100%;
`;

const RegionSelector = styled.div`
  display: flex;
  align-items: center;

  /* Improve contrast and clarity of the region dropdown */
  .MuiFormControl-root,
  .MuiInputBase-root {
    color: ${props => props.theme.text};
  }

  .MuiOutlinedInput-notchedOutline {
    border-color: ${props => props.theme.text};
  }

  .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline,
  .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline {
    border-color: ${props => props.theme.activeStateBlue};
  }

  .MuiSelect-icon {
    color: ${props => props.theme.text};
  }
`;

const HistoricalDatePickerContainer = styled.div`
  display: flex;
  align-items: center;
`;

const HistoricalUtilsProvider = styled(MuiPickersUtilsProvider)`
  margin-top: 8px;
`;

const HistoricalDatePicker = styled(KeyboardDatePicker)`
  &.MuiFormControl-marginNormal {
    margin-top: 0;
    margin-bottom: 0;
  }

  .MuiInputAdornment-positionEnd {
    margin-left: 0;
  }

  label {
    display: none;
  }

  input {
    opacity: 0;
    width: 0;
    padding: 0;
    pointer-events: none;
    visibility: hidden;
  }

  .MuiInputBase-root {
    margin-top: 0;
  }

  .MuiInput-underline:before {
    border-bottom: none;
    content: unset;
  }

  .MuiIconButton-root {
    color: ${props => props.theme.text};
    &:hover {
      color: ${props => props.theme.activeStateBlue};
    }
  }
`;

const MetricTitle = styled(SGTitle)`
  && {
    font-size: 0.8rem;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    opacity: 0.85;
    margin-bottom: 0.25rem;
    text-align: center;
  }
`;

const MetricValue = styled(SGBody)`
  && {
    font-size: clamp(1rem, 2.2vw, 1.5rem);
    font-weight: 800;
    line-height: 1.1;
    text-align: center;
  }
`;

const MetricPaper = styled(Paper)`
  padding: 0.2rem;
  min-height: 48px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const LoadingPaper = styled(Paper)`
  padding: 0.75rem;
  min-height: 72px;
`;

const GridContainer = styled(Grid)`
  padding: 0.5rem 0;
`;

const ChartContainer = styled.div`
  margin-top: 1rem;
`;

const SkeletonRounded = styled(Skeleton)`
  border-radius: 12px;
`;

const SwitchContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const SwitchLabel = styled.span`
  font-size: 0.875rem;
  font-family: 'Trade Gothic';
  color: ${props => props.theme.text};
`;

const MapHeaderContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.5rem;
`;

const ChartHeaderContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.5rem;
`;

const DeviceModalContent = styled(Paper)`
  width: 80vw;
  margin: 10vh auto;
  padding: 24px;
  outline: none;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  position: relative;
`;

const DeviceModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;

const DeviceModalDeviceList = styled.div`
  overflow-y: auto;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const DeviceModalMetricsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 20px;
`;

const DeviceModalMetricCard = styled.div`
  flex: 1;
  min-width: 180px;
  border: 2px solid ${props => props.theme.grey};
  border-radius: 8px;
  padding: 12px 16px;
  text-align: center;
  background: ${props => props.theme.cardBg};
`;

const DeviceModalMetricLabel = styled(SGBody)`
  && {
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.4px;
  }
`;

const DeviceModalMetricValue = styled(SGBody)`
  && {
    font-size: 1.5rem;
    font-weight: bold;
  }
`;

const ExportModalOverlay = styled(Modal)`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const ExportModalContent = styled(Paper)`
  max-width: 500px;
  padding: 2rem;
  position: relative;
  outline: none;
`;

const ExportModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const ExportModalTitle = styled(SGTitle)`
  margin: 0;
`;

const ExportModalBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const ExportOption = styled.div`
  display: flex;
  align-items: flex-start;
  padding: 1rem;
  border: 1px solid ${props => props.theme.grey};
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  background-color: ${props =>
    props.selected ? props.theme.activeStateBlue + '20' : 'transparent'};
  border-color: ${props => (props.selected ? props.theme.activeStateBlue : props.theme.grey)};

  &:hover {
    border-color: ${props => props.theme.activeStateBlue};
    background-color: ${props => props.theme.activeStateBlue + '10'};
  }

  input[type='radio'] {
    margin-right: 1rem;
    margin-top: 0.25rem;
    cursor: pointer;
  }
`;

const ExportOptionLabel = styled.label`
  cursor: pointer;
  display: flex;
  flex-direction: column;
  flex: 1;

  .option-title {
    font-weight: 600;
    font-family: 'Trade Gothic Bold Condensed';
    color: ${props => props.theme.text};
    margin-bottom: 0.25rem;
  }

  .option-description {
    font-size: 0.875rem;
    color: ${props => props.theme.subtleText};
    font-family: 'Trade Gothic';
  }
`;

const ExportModalFooter = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
`;

const ExportModalButton = styled(Button)`
  text-transform: capitalize;
  font-family: 'Trade Gothic Bold Condensed';
  border-radius: 8px;
  padding: 8px 16px;
  font-size: 0.875rem;

  &.primary {
    background-color: ${props => props.theme.exportButtonBg || props.theme.activeStateBlue};
    color: ${props => props.theme.defaultNavy};
    &:hover {
      background-color: ${props =>
        `${props.theme.exportButtonBg || props.theme.activeStateBlue}cc`};
    }
  }

  &.secondary {
    color: ${props => props.theme.text};
    border: 1px solid ${props => props.theme.grey};
    &:hover {
      background-color: ${props => props.theme.grey}20;
    }
  }
`;

const CloseButton = styled(Button)`
  position: absolute;
  right: 1rem;
  top: 1rem;
  min-width: unset;
  padding: 4px 8px;
  color: ${props => props.theme.text};
  font-size: 1.5rem;
  line-height: 1;
  &:hover {
    color: ${props => props.theme.focusText};
  }
`;

const SELECTED_REGION_CODE = {
  'North America': 'NA',
  EMEA: 'EMEA',
  APLA: 'APLA',
  'Greater China': 'GC',
};

const REGIONS_ALL = ['North America', 'EMEA', 'APLA', 'Greater China'];

const HEALTH_THRESHOLD_HIGH = 70;
const HEALTH_THRESHOLD_LOW = 30;

const buildRecentDateOptions = daysBack => {
  const today = new Date();
  const options = [];
  for (let i = 0; i < daysBack; i++) {
    const d = new Date(today);
    d.setDate(today.getDate() - i);
    const iso = d.toISOString().split('T')[0];
    options.push(iso);
  }
  return options;
};

const HISTORICAL_DATE_OPTIONS = buildRecentDateOptions(7);

const MIN_HEIGHT_EXPORTING = 180;
const MIN_HEIGHT_DEFAULT = 140;
const EXPORT_COOLDOWN_MS = 8000;

const formatDeviceInventoryStatus = status => {
  const normalized = (status ?? '').toString().trim();
  if (!normalized) return 'Unknown';
  return normalized.replace(/([a-z])([A-Z])/g, '$1 $2');
};

const formatDeviceLastSeen = value => {
  if (!value) return 'Unknown';
  const dateObj = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(dateObj.getTime())) return 'Unknown';
  const datePart = dateObj.toLocaleDateString();
  const timePart = dateObj.toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'});
  return `${datePart} ${timePart}`;
};

const StoreHealth = () => {
  const location = useLocation();
  const crumbMatch = {url: location?.pathname || '/retail-hardware-status'};
  const [selectedRegion, setSelectedRegion] = useState(() => {
    const persisted = sessionStorage.getItem('storeHealth:selectedRegion');
    return persisted && REGIONS_ALL.includes(persisted) ? persisted : 'North America';
  });
  const [storeData, setStoreData] = useState();
  const [viewMode, setViewMode] = useState(false); // false = Network Status, true = ITAM Mismatch
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [exportOption, setExportOption] = useState('full'); // 'inventory_mismatch' | 'network_inactive' | 'inventory_or_inactive' | 'full'
  const [historicalModalOpen, setHistoricalModalOpen] = useState(false);
  const [historicalRegion, setHistoricalRegion] = useState(() => {
    const persisted = sessionStorage.getItem('storeHealth:selectedRegion');
    return persisted && REGIONS_ALL.includes(persisted) ? persisted : 'North America';
  });
  const [historicalDate, setHistoricalDate] = useState(() => HISTORICAL_DATE_OPTIONS[0]);
  const [historicalExporting, setHistoricalExporting] = useState(false);
  const [deviceModalOpen, setDeviceModalOpen] = useState(false);
  const [selectedChartDeviceType, setSelectedChartDeviceType] = useState('');
  const [selectedChartDevices, setSelectedChartDevices] = useState([]);
  const RetailDeviceDataPending = useSelector(state =>
    rdxRetailDeviceDataPending(state, SELECTED_REGION_CODE[selectedRegion])
  );
  const RetailDeviceData = useSelector(state =>
    rdxRetailDeviceData(state, SELECTED_REGION_CODE[selectedRegion])
  );
  const historyStoreDataPending = useSelector(state => state.data?.api?.historyStoreData?.pending);
  const historyStoreData = useSelector(state => state.data?.app?.historyStoreData?.data);
  const {oktaAuth} = useOktaAuth();
  const themeColors = useTheme();
  const themedSelectMenuProps = useMemo(() => getThemedSelectMenuProps(themeColors), [themeColors]);
  const themedSelectMenuPropsInModal = useMemo(
    () => getThemedSelectMenuProps(themeColors, {inModal: true}),
    [themeColors]
  );

  const accessToken = oktaAuth.getAccessToken();
  const dispatch = useDispatch();

  const {
    logDashboardLand,
    logRegionChange,
    logHistoricalModalOpen,
    logHistoricalDateChange,
    logHistoricalRegionChange,
    logExport,
  } = useRetailHardwareStatusLogs();

  const dashboardLandLoggedRef = useRef(false);

  const historicalDisplayDate = useMemo(() => {
    const base = historicalDate || HISTORICAL_DATE_OPTIONS[0];
    const parsed = new Date(base);
    if (Number.isNaN(parsed.getTime())) {
      return base;
    }
    return parsed.toLocaleDateString('en-US');
  }, [historicalDate]);

  const incomingRegion = location?.state?.selectedRegion;
  useEffect(() => {
    if (incomingRegion && REGIONS_ALL.includes(incomingRegion)) {
      setSelectedRegion(incomingRegion);
    }
  }, [incomingRegion]);

  useEffect(() => {
    if (selectedRegion) {
      sessionStorage.setItem('storeHealth:selectedRegion', selectedRegion);
    }
  }, [selectedRegion]);

  useEffect(() => {
    if (!historicalModalOpen) {
      setHistoricalRegion(selectedRegion);
    }
  }, [selectedRegion, historicalModalOpen]);

  useEffect(() => {
    if (!accessToken || dashboardLandLoggedRef.current) return;
    dashboardLandLoggedRef.current = true;
    logDashboardLand({
      view: 'global',
      region: selectedRegion,
      geoCode: SELECTED_REGION_CODE[selectedRegion],
    });
    // Intentionally once when token is ready; region at land is captured here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, logDashboardLand]);

  useEffect(() => {
    if (!accessToken) return;
    dispatch(getRetailStoresData(accessToken));

    fetch(config.resourceServer.insightsApiUrlStoresData, {
      mode: 'cors',
      credentials: 'include',
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    })
      .then(response => {
        if (!response.ok) {
          return Promise.reject(response);
        }
        return response.json();
      })
      .then(data => {
        const result = data.map(({StoreNumber, StoreName, Longitude, Latitude, Geo, SiteId}) => ({
          StoreNumber,
          StoreName,
          Longitude,
          Latitude,
          Geo,
          siteId: SiteId,
        }));
        setStoreData(result);
      })
      .catch(err => {
        console.error('Failed to fetch store data:', err);
      });
  }, [dispatch, accessToken]);

  const totalStoresList = useMemo(() => new Set(RetailDeviceData?.map(item => item?.siteId)), [
    RetailDeviceData,
  ]);

  const totalStores = useMemo(() => totalStoresList.size, [totalStoresList]);
  const deviceNotSeenCount = useMemo(
    () => RetailDeviceData?.filter(data => !data?.isRecentlySeen)?.length,
    [RetailDeviceData]
  );

  const ItamMismatchCount = useMemo(
    () => RetailDeviceData?.filter(data => data?.itamInventoryStatus !== 'MatchFound')?.length,
    [RetailDeviceData]
  );

  const getDeviceFailingPing = useCallback(() => {
    let deviceFailing = {};

    const RetailDeviceNotWorking = RetailDeviceData?.filter(data => !data?.isRecentlySeen);
    if (RetailDeviceNotWorking) {
      RetailDeviceNotWorking.forEach(data => {
        deviceFailing[data?.deviceType] = (deviceFailing[data?.deviceType] || 0) + 1;
      });
    }
    const sortedObj = Object.fromEntries(Object.entries(deviceFailing).sort((a, b) => b[1] - a[1]));
    return sortedObj;
  }, [RetailDeviceData]);

  const getDeviceFailingItam = useCallback(() => {
    let deviceFailing = {};

    const RetailDeviceNotWorking = RetailDeviceData?.filter(
      data => data?.itamInventoryStatus !== 'MatchFound'
    );
    if (RetailDeviceNotWorking) {
      RetailDeviceNotWorking.forEach(data => {
        deviceFailing[data?.deviceType] = (deviceFailing[data?.deviceType] || 0) + 1;
      });
    }
    const sortedObj = Object.fromEntries(Object.entries(deviceFailing).sort((a, b) => b[1] - a[1]));
    return sortedObj;
  }, [RetailDeviceData]);

  const getMostImpactedStores = useCallback(() => {
    const finalData = storeData
      ?.filter(store => store?.Geo === SELECTED_REGION_CODE[selectedRegion])
      ?.map(store => {
        const relatedDevices = RetailDeviceData?.filter(device => device?.siteId === store?.siteId);
        // if (!RetailDeviceData.find(deviceStore => deviceStore?.siteId === store?.siteId)) return null;
        if (
          relatedDevices?.length === 0 ||
          (!relatedDevices?.filter(d => d?.itamInventoryStatus !== 'MatchFound')?.length &&
            !relatedDevices?.filter(d => !d?.isRecentlySeen)?.length)
        )
          return null;

        return {
          siteId: store?.siteId,
          StoreName: store?.StoreName,
          Longitude: store?.Longitude,
          Latitude: store?.Latitude,
          totalDevicesList: relatedDevices?.length || 0,
          impactedDevicesCount: relatedDevices?.filter(d => !d?.isDeviceHealthy)?.length || 0,
          pingIssueCount: relatedDevices?.filter(d => !d?.isRecentlySeen)?.length || 0,
          itamMismatchCount:
            relatedDevices?.filter(d => d?.itamInventoryStatus !== 'MatchFound')?.length || 0,
        };
      })
      .filter(store => store !== null); // Remove null entries for stores with no devices

    return finalData;
  }, [storeData, selectedRegion, RetailDeviceData]);

  const storeHealth = useCallback((totalDevices, ImpactedDevices) => {
    const healthPercentage = ((totalDevices - ImpactedDevices) / totalDevices) * 100;
    if (healthPercentage && healthPercentage >= HEALTH_THRESHOLD_HIGH) return '#0f820fff';
    else if (healthPercentage && healthPercentage > HEALTH_THRESHOLD_LOW) return '#dede1dff';
    else if (healthPercentage !== undefined && healthPercentage <= HEALTH_THRESHOLD_LOW)
      return '#bb1c1cff';
    else return '#bb1c1cff';
  }, []);

  const getStoreMapData = useCallback(() => {
    if (!RetailDeviceDataPending && (!RetailDeviceData || RetailDeviceData.length === 0)) return [];
    return getMostImpactedStores()?.map(store => ({
      latitude: Number(store?.Latitude),
      longitude: Number(store?.Longitude),
      title: store?.StoreName,
      url: `/store/${store?.siteId}/retail-hardware-status`,
      color: storeHealth(
        store?.totalDevicesList,
        viewMode ? store?.itamMismatchCount : store?.pingIssueCount
      ),
    }));
  }, [RetailDeviceDataPending, RetailDeviceData, getMostImpactedStores, storeHealth, viewMode]);

  const memoizedStoreMapData = useMemo(
    () => (getStoreMapData()?.length > 0 ? getStoreMapData() : []),
    [getStoreMapData]
  );

  const deviceFailingPingData = useMemo(() => getDeviceFailingPing(), [getDeviceFailingPing]);
  const deviceFailingItamData = useMemo(() => getDeviceFailingItam(), [getDeviceFailingItam]);

  const storeBySiteId = useMemo(() => {
    if (!storeData) return new Map();
    const map = new Map();
    storeData.forEach(store => {
      if (store?.siteId != null) {
        map.set(store.siteId, store);
      }
    });
    return map;
  }, [storeData]);

  const handleChartBarClick = useCallback(
    deviceType => {
      if (!deviceType || !RetailDeviceData) return;
      const failingDevices = viewMode
        ? RetailDeviceData.filter(
            d => d?.deviceType === deviceType && d?.itamInventoryStatus !== 'MatchFound'
          )
        : RetailDeviceData.filter(d => d?.deviceType === deviceType && !d?.isRecentlySeen);

      const devicesWithStore = failingDevices.map(device => {
        const store = storeBySiteId.get(device?.siteId);
        return {
          ...device,
          storeName: store?.StoreName || device?.siteId || 'N/A',
          networkCheckStatus: device?.isRecentlySeen ? 'Active' : 'Inactive',
          inventoryCheckStatus: formatDeviceInventoryStatus(device?.itamInventoryStatus),
          lastSeenDate: device?.lastSeen || null,
        };
      });

      setSelectedChartDeviceType(deviceType);
      setSelectedChartDevices(devicesWithStore);
      setDeviceModalOpen(true);
    },
    [RetailDeviceData, viewMode, storeBySiteId]
  );

  const chartContainerRef = useRef(null);

  useEffect(() => {
    const el = chartContainerRef.current;
    if (!el) return;

    const handleLabelClick = event => {
      let target = event.target;
      const tag = (target.localName || target.tagName || '').toLowerCase();
      if (tag === 'tspan' || tag === 'title') {
        target = target.parentElement;
      }
      const targetTag = (target?.localName || target?.tagName || '').toLowerCase();
      if (targetTag !== 'text') return;

      const xaxisGroup = el.querySelector('.apexcharts-xaxis-texts-g');
      if (!xaxisGroup || !xaxisGroup.contains(target)) return;

      const textElements = Array.from(xaxisGroup.querySelectorAll(':scope > text'));
      const index = textElements.indexOf(target);
      if (index < 0) return;

      const categories = Object.keys(viewMode ? deviceFailingItamData : deviceFailingPingData);
      const deviceType = categories[index];
      if (deviceType) {
        handleChartBarClick(deviceType);
      }
    };

    el.addEventListener('click', handleLabelClick);
    return () => el.removeEventListener('click', handleLabelClick);
  }, [viewMode, deviceFailingItamData, deviceFailingPingData, handleChartBarClick]);

  const chartOptions = useMemo(
    () => ({
      chart: {
        id: 'basic-bar',
        events: {
          dataPointSelection: (_event, _chartContext, config) => {
            const categories = Object.keys(
              viewMode ? deviceFailingItamData : deviceFailingPingData
            );
            const deviceType = categories[config.dataPointIndex];
            if (deviceType) {
              handleChartBarClick(deviceType);
            }
          },
        },
      },
      xaxis: {
        categories: Object.keys(viewMode ? deviceFailingItamData : deviceFailingPingData),
        labels: {
          style: {
            fontSize: '12px',
            textWrap: 'wrap',
          },
          trim: false,
          rotate: -45,
          rotateAlways: false,
          hideOverlappingLabels: true,
          maxHeight: 120,
        },
      },
      plotOptions: {
        bar: {
          dataLabels: {
            position: 'top',
          },
        },
      },
      states: {
        hover: {
          filter: {
            type: 'darken',
            value: 0.85,
          },
        },
      },
    }),
    [deviceFailingItamData, deviceFailingPingData, viewMode, handleChartBarClick]
  );

  const tableColumns = [
    {
      id: 'StoreName',
      label: 'Store Name',
      width: '30%',
      align: 'left',
      icon: 'none',
      format: (value, row) => (
        <Link
          to={{
            pathname: `/store/${row.siteId}/retail-hardware-status`,
            state: {selectedRegion},
          }}
          style={{textDecoration: 'none', color: 'inherit'}}
        >
          <TableText style={{fontWeight: row.isTotal ? 'bold' : 'normal', cursor: 'pointer'}}>
            {value}
          </TableText>
        </Link>
      ),
    },
    {
      id: 'pingIssueCount',
      label: 'Inactive Devices Count',
      width: '25%',
      align: 'right',
      icon: 'none',
      format: (value, row) => (
        <TableText style={{fontWeight: row.isTotal ? 'bold' : 'normal'}}>{value}</TableText>
      ),
      sortable: true,
    },
    {
      id: 'itamMismatchCount',
      label: 'Itam Mismatch Devices Count',
      width: '25%',
      align: 'right',
      icon: 'none',
      format: (value, row) => (
        <TableText style={{fontWeight: row.isTotal ? 'bold' : 'normal'}}>{value}</TableText>
      ),
      sortable: true,
    },
  ];
  const deviceModalColumns = [
    {
      id: 'storeName',
      label: 'Store Name',
      width: '12%',
      align: 'left',
      icon: 'none',
      format: (value, row) => (
        <Link
          to={{
            pathname: `/store/${row.siteId}/retail-hardware-status`,
            state: {selectedRegion},
          }}
          style={{textDecoration: 'underline', color: themeColors.blue}}
        >
          <TableText style={{cursor: 'pointer'}}>{value || 'N/A'}</TableText>
        </Link>
      ),
    },
    {
      id: 'deviceName',
      label: 'Device Name',
      width: '13%',
      align: 'left',
      icon: 'none',
      format: value => <TableText>{value || 'Unknown'}</TableText>,
    },
    {
      id: 'serialNumber',
      label: 'Serial Number',
      width: '12%',
      align: 'left',
      icon: 'none',
      format: value => <TableText>{value || 'Unknown'}</TableText>,
    },
    {
      id: 'model',
      label: 'Model',
      width: '12%',
      align: 'left',
      icon: 'none',
      format: value => <TableText>{value || 'Unknown'}</TableText>,
    },
    {
      id: 'networkCheckStatus',
      label: 'Network Status',
      width: '10%',
      align: 'left',
      icon: 'none',
      format: value => <TableText>{value}</TableText>,
    },
    {
      id: 'lastSeenDate',
      label: 'Last Seen',
      width: '12%',
      align: 'left',
      icon: 'none',
      format: value => (
        <TableText style={{whiteSpace: 'pre-line'}}>{formatDeviceLastSeen(value)}</TableText>
      ),
    },
    {
      id: 'inventoryCheckStatus',
      label: 'ServiceNow Inventory Status',
      width: '14%',
      align: 'left',
      icon: 'none',
      format: value => <TableText>{value}</TableText>,
    },
    {
      id: 'itamInventoryStatusDescription',
      label: 'ServiceNow Inventory Status Description',
      width: '15%',
      align: 'left',
      icon: 'none',
      format: value => (
        <div
          style={{
            maxHeight: '6rem',
            overflowY: 'auto',
            wordBreak: 'break-word',
            whiteSpace: 'pre-wrap',
            paddingRight: 4,
          }}
        >
          <TableText>{value || ''}</TableText>
        </div>
      ),
    },
  ];

  const handleDeviceModalClose = () => {
    setDeviceModalOpen(false);
  };

  const deviceModalTotalCount = useMemo(
    () => RetailDeviceData?.filter(d => d?.deviceType === selectedChartDeviceType)?.length || 0,
    [RetailDeviceData, selectedChartDeviceType]
  );

  const deviceModalImpactedCount = useMemo(
    () =>
      selectedChartDevices.filter(
        d => !d?.isRecentlySeen || d?.itamInventoryStatus !== 'MatchFound'
      ).length,
    [selectedChartDevices]
  );

  const handleChange = e => {
    const newRegion = e.target.value;
    const previousRegion = selectedRegion;
    sessionStorage.setItem('storeHealth:selectedRegion', newRegion);
    setSelectedRegion(newRegion);
    logRegionChange({
      previousRegion,
      newRegion,
      geoCode: SELECTED_REGION_CODE[newRegion],
    });
  };

  const handleExportModalOpen = () => {
    if (!RetailDeviceData || RetailDeviceData.length === 0) {
      alert('No data available to export');
      return;
    }
    setExportModalOpen(true);
  };

  const handleExportModalClose = () => {
    setExportModalOpen(false);
  };

  const handleHistoricalModalOpen = () => {
    logHistoricalModalOpen({
      dashboardRegion: selectedRegion,
      initialHistoricalRegion: historicalRegion,
      initialHistoricalDate: historicalDate || HISTORICAL_DATE_OPTIONS[0],
      geoCode: SELECTED_REGION_CODE[historicalRegion],
    });
    setHistoricalModalOpen(true);
  };

  const handleHistoricalModalClose = () => {
    setHistoricalModalOpen(false);
    setHistoricalDate(HISTORICAL_DATE_OPTIONS[0]);
  };

  useEffect(() => {
    if (!historicalModalOpen || !accessToken) return;
    if (!historicalRegion || !historicalDate) return;

    const geoCode = SELECTED_REGION_CODE[historicalRegion];
    dispatch(
      fetchHistoryStoreData(accessToken, {
        date: historicalDate,
        geo: geoCode,
      })
    );
  }, [historicalModalOpen, historicalRegion, historicalDate, accessToken, dispatch]);

  const mapDeviceToExportRow = device => {
    const matchingStore = storeBySiteId.get(device?.siteId);
    return {
      Region: selectedRegion,
      Company: device?.brand || 'N/A',
      'Store Number': matchingStore?.StoreNumber || 'N/A',
      'Store Name': matchingStore?.StoreName || 'N/A',
      'Device Category': device?.deviceType,
      'Device Name': device?.deviceName || 'N/A',
      'Device Serial Number': device?.serialNumber || 'N/A',
      'Inventory Check Status': device?.itamInventoryStatus || 'N/A',
      'Inventory Check Status Description': device?.itamInventoryStatusDescription || 'N/A',
      'Network Connectivity Check Status': device?.isRecentlySeen ? 'Active' : 'Inactive',
      'Last Seen':
        device?.deviceType === 'MobilePaymentTerminal'
          ? 'Not Available'
          : device?.lastSeen || 'N/A',
    };
  };

  const handleExportConfirm = () => {
    if (!RetailDeviceData || RetailDeviceData.length === 0) {
      alert('No data available to export');
      return;
    }

    let filteredDevices;
    switch (exportOption) {
      case 'inventory_mismatch':
        filteredDevices = RetailDeviceData?.filter(
          device => device?.itamInventoryStatus !== 'MatchFound'
        );
        break;
      case 'network_inactive':
        filteredDevices = RetailDeviceData?.filter(device => !device?.isRecentlySeen);
        break;
      case 'inventory_or_inactive':
        filteredDevices = RetailDeviceData?.filter(
          device => device?.itamInventoryStatus !== 'MatchFound' || !device?.isRecentlySeen
        );
        break;
      case 'full':
      default:
        filteredDevices = RetailDeviceData;
        break;
    }

    const exportData = filteredDevices?.map(mapDeviceToExportRow) ?? [];
    logExport({
      exportContext: 'dashboard',
      exportOption,
      region: selectedRegion,
      geoCode: SELECTED_REGION_CODE[selectedRegion],
      deviceCount: exportData.length,
    });
    performExport(exportData, exportOption);
    setExportModalOpen(false);
  };

  const performExport = async (exportData, exportOption) => {
    const columnWidths = [
      {wch: 12}, // Region
      {wch: 15}, // Company
      {wch: 12}, // Store Number
      {wch: 40}, // Store Name
      {wch: 25}, // Device Category
      {wch: 30}, // Device Name
      {wch: 18}, // Device Serial Number
      {wch: 20}, // Inventory Check Status
      {wch: 50}, // Inventory Check Status Description
      {wch: 30}, // Network Connectivity Check Status
      {wch: 27}, // Last Seen
    ];

    const date = new Date().toISOString().split('T')[0];
    const filename = `RetailDevices_${SELECTED_REGION_CODE[selectedRegion]}_${date}_${exportOption}.xlsx`;

    const workbook = createWorkbook();
    addJsonSheet(workbook, 'Retail Devices', exportData, columnWidths);
    await downloadWorkbook(workbook, filename);
  };

  const historicalSummaryRow = useMemo(() => {
    if (!Array.isArray(historyStoreData) || historyStoreData.length === 0) {
      return null;
    }

    const geoCode = SELECTED_REGION_CODE[historicalRegion];

    // Filter only records for the currently selected region/geo.
    const regionItems = historyStoreData.filter(item => {
      const itemGeo = item?.geo || item?.Geo;
      return !geoCode || !itemGeo ? true : itemGeo === geoCode;
    });

    if (!regionItems.length) return null;

    // Total stores = unique siteId values for the region.
    const uniqueSiteIds = new Set(
      regionItems
        .map(item => item?.siteId || item?.SiteId)
        .filter(siteId => typeof siteId === 'string' && siteId.trim().length > 0)
    );

    // Impacted devices: active devices that are either not recently seen or not healthy.
    const impactedDevices = regionItems.reduce((count, item) => {
      const isActive = !!item?.isActive;
      const isRecentlySeen = !!item?.isRecentlySeen;
      const isDeviceHealthy = !!item?.isDeviceHealthy;
      const isImpacted = isActive && (!isRecentlySeen || !isDeviceHealthy);
      return count + (isImpacted ? 1 : 0);
    }, 0);

    return {
      id: 'historical-summary',
      region: historicalRegion,
      totalStores: uniqueSiteIds.size,
      impactedDevices,
    };
  }, [historyStoreData, historicalRegion]);

  const handleHistoricalRowExport = async () => {
    if (historicalExporting) return;
    if (!Array.isArray(historyStoreData) || historyStoreData.length === 0) {
      return;
    }

    const geoCode = SELECTED_REGION_CODE[historicalRegion];
    const regionItems = historyStoreData.filter(item => {
      const itemGeo = item?.geo || item?.Geo;
      return !geoCode || !itemGeo ? true : itemGeo === geoCode;
    });

    if (!regionItems.length) {
      return;
    }

    logExport({
      exportContext: 'historical',
      historicalRegion,
      historicalDate: historicalDate || '',
      geoCode,
      rowCount: regionItems.length,
    });

    setHistoricalExporting(true);
    try {
      const storeBySiteId = Array.isArray(storeData)
        ? storeData.reduce((acc, store) => {
            const siteId = store?.siteId;
            if (siteId != null) {
              acc.set(siteId, store);
            }
            return acc;
          }, new Map())
        : new Map();

      const exportRows = regionItems.map(item => {
        const matchingStore = storeBySiteId.get(item?.siteId);

        return {
          Region: historicalRegion || '',
          'Status Check Date': historicalDate || '',
          Brand: item?.brand || '',
          'Store Type': item?.siteType || '',
          'Store Number': matchingStore?.StoreNumber || '',
          'Store Name': matchingStore?.StoreName || '',
          'Device Category': item?.deviceType || '',
          Manufacturer: item?.manufacturer || '',
          'Device Name': item?.deviceName || '',
          'Device Serial Number': item?.serialNumber || '',
          'Inventory Check Status': item?.itamInventoryStatus || '',
          'Inventory Check Status Description': item?.itamInventoryStatusDescription || '',
          'Network Connectivity Check Status': item?.isRecentlySeen ? 'Active' : 'Inactive',
          'Last Seen':
            item?.deviceType === 'MobilePaymentTerminal' ? 'Not Available' : item?.lastSeen || '',
        };
      });
      // Set column widths for better readability
      const columnWidths = [
        {wch: 15}, // Region
        {wch: 15}, // Status Check Date
        {wch: 10}, // Brand
        {wch: 10}, // Store Type
        {wch: 12}, // Store Number
        {wch: 40}, // Store Name
        {wch: 25}, // Device Category
        {wch: 15}, // Manufacturer
        {wch: 20}, // Device Name
        {wch: 35}, // Device Serial Number
        {wch: 25}, // ITAM Inventory Status
        {wch: 50}, // Inventory Check Status Description
        {wch: 30}, // Network Status
        {wch: 27}, // Last Seen
      ];

      const workbook = createWorkbook();
      addJsonSheet(workbook, 'Historical Devices', exportRows, columnWidths);

      const filename = `RetailDevices_History_${SELECTED_REGION_CODE[historicalRegion]}_${historicalDate}_all.xlsx`;
      await downloadWorkbook(workbook, filename);
    } finally {
      // Keep the export button disabled briefly so users don't spam-click
      // while a large file is being prepared.
      setTimeout(() => {
        setHistoricalExporting(false);
      }, EXPORT_COOLDOWN_MS);
    }
  };

  return (
    <>
      <StyledLayout
        headerContent={
          <>
            <LayoutHeader
              title={'Retail Hardware Status'}
              icon={LayoutHeaderIconTypes.StoreHealth}
            />
          </>
        }
      >
        <Container>
          <div style={{marginBottom: '1rem', marginTop: '-0.5rem', fontSize: '1rem'}}>
            <NavCrumb match={crumbMatch} breadCrumb className="storehealth-breadcrumb" />
          </div>
          <TitleSection className="Title">
            <SGHeadline className={'text-color'} variant={'h5'}>
              Dashboard Global View
            </SGHeadline>
          </TitleSection>
          <div className="regionMetrics">
            <HeaderSection className="header">
              <RegionSelector>
                <div>
                  <SGTitle className={'active-color'}>Select Region</SGTitle>
                  <Select
                    autoWidth
                    value={selectedRegion}
                    onChange={handleChange}
                    label={'Select-Region'}
                    MenuProps={themedSelectMenuProps}
                  >
                    {REGIONS_ALL.map(val => (
                      <ThemedSelectMenuItem key={val} value={val}>
                        {val}
                      </ThemedSelectMenuItem>
                    ))}
                  </Select>
                </div>
                <ExportButton
                  startIcon={<Icon path={mdiDownloadOutline} size={0.8} />}
                  onClick={handleExportModalOpen}
                  disabled={
                    RetailDeviceDataPending || !RetailDeviceData || RetailDeviceData.length === 0
                  }
                >
                  Export to Excel
                </ExportButton>
                <ExportButton
                  onClick={handleHistoricalModalOpen}
                  disabled={historyStoreDataPending}
                >
                  Historical Data
                </ExportButton>
                <ExportModalOverlay
                  open={historicalModalOpen}
                  onClose={handleHistoricalModalClose}
                  closeAfterTransition
                  BackdropComponent={Backdrop}
                  BackdropProps={{
                    timeout: 500,
                  }}
                >
                  <Fade in={historicalModalOpen}>
                    <ExportModalContent>
                      <ExportModalHeader>
                        <ExportModalTitle className={'active-color'}>
                          Historical Data
                        </ExportModalTitle>
                        <CloseButton onClick={handleHistoricalModalClose}>✕</CloseButton>
                      </ExportModalHeader>
                      <ExportModalBody>
                        <RegionSelector style={{display: 'flex', gap: '1rem', flexWrap: 'wrap'}}>
                          <div>
                            <SGTitle className={'active-color'}>Date</SGTitle>
                            <HistoricalDatePickerContainer>
                              <InsightsPickersThemeProvider>
                                <HistoricalUtilsProvider utils={DateFnsUtils}>
                                  <HistoricalDatePicker
                                    disableToolbar
                                    disableFuture
                                    autoOk
                                    minDate={new Date('2026-01-14T00:00:00')}
                                    variant="inline"
                                    format="MM/dd/yyyy"
                                    margin="normal"
                                    id="storehealth-historical-date"
                                    label="Date"
                                    value={historicalDate ? new Date(historicalDate) : new Date()}
                                    onChange={date => {
                                      const pickedDate =
                                        date instanceof Date ? date : date ? new Date(date) : null;
                                      if (pickedDate && !Number.isNaN(pickedDate.getTime())) {
                                        const iso = pickedDate.toISOString().slice(0, 10);
                                        setHistoricalDate(iso);
                                        logHistoricalDateChange({
                                          historicalDate: iso,
                                          historicalRegion,
                                          geoCode: SELECTED_REGION_CODE[historicalRegion],
                                        });
                                      }
                                    }}
                                    KeyboardButtonProps={{
                                      'aria-label': 'Select historical date',
                                    }}
                                    keyboardIcon={
                                      <span
                                        style={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: 4,
                                          color: 'inherit',
                                        }}
                                      >
                                        <span style={{fontSize: '0.8rem', marginLeft: '-0.5rem'}}>
                                          {historicalDisplayDate}
                                        </span>
                                        <DateRangeIcon />
                                      </span>
                                    }
                                  />
                                </HistoricalUtilsProvider>
                              </InsightsPickersThemeProvider>
                            </HistoricalDatePickerContainer>
                          </div>
                          <div>
                            <SGTitle className={'active-color'}>Region</SGTitle>
                            <Select
                              autoWidth
                              value={historicalRegion}
                              onChange={e => {
                                const next = e.target.value;
                                setHistoricalRegion(next);
                                logHistoricalRegionChange({
                                  historicalRegion: next,
                                  geoCode: SELECTED_REGION_CODE[next],
                                  historicalDate: historicalDate || HISTORICAL_DATE_OPTIONS[0],
                                });
                              }}
                              label="Historical Region"
                              MenuProps={themedSelectMenuPropsInModal}
                            >
                              {REGIONS_ALL.map(val => (
                                <ThemedSelectMenuItem key={val} value={val}>
                                  {val}
                                </ThemedSelectMenuItem>
                              ))}
                            </Select>
                          </div>
                        </RegionSelector>
                        <div style={{marginTop: '1.5rem'}}>
                          <SGTitle className={'active-color'} style={{marginBottom: '0.5rem'}}>
                            Historical Summary
                          </SGTitle>
                          <Paper
                            style={{
                              padding: '0.5rem 0.75rem',
                              minHeight: historicalExporting
                                ? MIN_HEIGHT_EXPORTING
                                : MIN_HEIGHT_DEFAULT,
                            }}
                          >
                            <Table
                              columns={[
                                {
                                  id: 'region',
                                  label: 'Region',
                                  width: '30%',
                                  align: 'left',
                                  icon: 'none',
                                  format: value => <TableText>{value}</TableText>,
                                },
                                {
                                  id: 'totalStores',
                                  label: 'Total Stores',
                                  width: '25%',
                                  align: 'right',
                                  icon: 'none',
                                  format: value => <TableText>{value}</TableText>,
                                },
                                {
                                  id: 'impactedDevices',
                                  label: 'Impacted Devices',
                                  width: '25%',
                                  align: 'right',
                                  icon: 'none',
                                  format: value => <TableText>{value}</TableText>,
                                },
                                {
                                  id: 'actions',
                                  label: 'Actions',
                                  width: '20%',
                                  align: 'right',
                                  icon: 'none',
                                  format: () => (
                                    <div
                                      style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'flex-end',
                                        gap: '4px',
                                      }}
                                    >
                                      <ExportButton
                                        onClick={handleHistoricalRowExport}
                                        disabled={
                                          !historicalSummaryRow ||
                                          historicalExporting ||
                                          historyStoreDataPending
                                        }
                                      >
                                        {historicalExporting ? 'Downloading...' : 'Export'}
                                      </ExportButton>
                                      {historicalExporting && (
                                        <SGBody
                                          variant="b2"
                                          style={{
                                            fontSize: '0.7rem',
                                            opacity: 0.8,
                                            maxWidth: '220px',
                                            textAlign: 'right',
                                          }}
                                        >
                                          Large files can take a while; please wait until the
                                          download is complete before trying to export again.
                                        </SGBody>
                                      )}
                                    </div>
                                  ),
                                },
                              ]}
                              data={historicalSummaryRow ? [historicalSummaryRow] : []}
                              loaded={!historyStoreDataPending}
                              noDataText="No historical data available"
                              disablePagination
                            />
                          </Paper>
                        </div>
                      </ExportModalBody>
                      <ExportModalFooter>
                        <ExportModalButton
                          className="secondary"
                          onClick={handleHistoricalModalClose}
                        >
                          Close
                        </ExportModalButton>
                      </ExportModalFooter>
                    </ExportModalContent>
                  </Fade>
                </ExportModalOverlay>
                <ExportModalOverlay
                  open={exportModalOpen}
                  onClose={handleExportModalClose}
                  closeAfterTransition
                  BackdropComponent={Backdrop}
                  BackdropProps={{
                    timeout: 500,
                  }}
                >
                  <Fade in={exportModalOpen}>
                    <ExportModalContent>
                      <ExportModalHeader>
                        <ExportModalTitle className={'active-color'}>
                          Export Options
                        </ExportModalTitle>
                        <CloseButton
                          onClick={handleExportModalClose}
                          aria-label="Close export options"
                        >
                          ✕
                        </CloseButton>
                      </ExportModalHeader>
                      <ExportModalBody>
                        <ExportOption selected={exportOption === 'inventory_mismatch'}>
                          <input
                            type="radio"
                            id="inventory-mismatch-export"
                            name="export-option"
                            value="inventory_mismatch"
                            checked={exportOption === 'inventory_mismatch'}
                            onChange={e => setExportOption(e.target.value)}
                          />
                          <ExportOptionLabel htmlFor="inventory-mismatch-export">
                            <div className="option-title">
                              Devices with inventory mismatch status
                            </div>
                            <div className="option-description">
                              Export only devices with ServiceNow inventory status mismatch
                            </div>
                          </ExportOptionLabel>
                        </ExportOption>
                        <ExportOption selected={exportOption === 'network_inactive'}>
                          <input
                            type="radio"
                            id="network-inactive-export"
                            name="export-option"
                            value="network_inactive"
                            checked={exportOption === 'network_inactive'}
                            onChange={e => setExportOption(e.target.value)}
                          />
                          <ExportOptionLabel htmlFor="network-inactive-export">
                            <div className="option-title">Devices with network inactive status</div>
                            <div className="option-description">
                              Export only devices that are inactive in the network
                            </div>
                          </ExportOptionLabel>
                        </ExportOption>
                        <ExportOption selected={exportOption === 'inventory_or_inactive'}>
                          <input
                            type="radio"
                            id="inventory-or-inactive-export"
                            name="export-option"
                            value="inventory_or_inactive"
                            checked={exportOption === 'inventory_or_inactive'}
                            onChange={e => setExportOption(e.target.value)}
                          />
                          <ExportOptionLabel htmlFor="inventory-or-inactive-export">
                            <div className="option-title">
                              Devices with inventory mismatch and/or inactive status
                            </div>
                            <div className="option-description">
                              Export devices with inventory mismatch or network inactive
                            </div>
                          </ExportOptionLabel>
                        </ExportOption>
                        <ExportOption selected={exportOption === 'full'}>
                          <input
                            type="radio"
                            id="full-export"
                            name="export-option"
                            value="full"
                            checked={exportOption === 'full'}
                            onChange={e => setExportOption(e.target.value)}
                          />
                          <ExportOptionLabel htmlFor="full-export">
                            <div className="option-title">Full list of devices</div>
                            <div className="option-description">
                              Export complete data including all devices
                            </div>
                          </ExportOptionLabel>
                        </ExportOption>
                      </ExportModalBody>
                      <ExportModalFooter>
                        <ExportModalButton className="secondary" onClick={handleExportModalClose}>
                          Cancel
                        </ExportModalButton>
                        <ExportModalButton className="primary" onClick={handleExportConfirm}>
                          Export
                        </ExportModalButton>
                      </ExportModalFooter>
                    </ExportModalContent>
                  </Fade>
                </ExportModalOverlay>
              </RegionSelector>
              <SearchStoreHealth region={SELECTED_REGION_CODE[selectedRegion]} />
            </HeaderSection>
            <GridContainer container spacing={2}>
              <Grid item xs>
                {RetailDeviceDataPending ? (
                  <LoadingPaper>
                    <Skeleton variant="text" width={120} height={32} />
                    <Skeleton variant="rect" width={80} height={32} />
                  </LoadingPaper>
                ) : (
                  <MetricPaper>
                    <MetricTitle className={'text-color'} variant={'s1'}>
                      Total Stores
                    </MetricTitle>
                    <MetricValue variant={'b1'} className={'active'}>
                      {totalStores > 0 ? totalStores : 'NA'}
                    </MetricValue>
                  </MetricPaper>
                )}
              </Grid>
              <Grid item xs>
                {RetailDeviceDataPending ? (
                  <LoadingPaper>
                    <Skeleton variant="text" width={120} height={32} />
                    <Skeleton variant="rect" width={80} height={32} />
                  </LoadingPaper>
                ) : (
                  <MetricPaper>
                    <MetricTitle className={'text-color'} variant={'s1'}>
                      Total Devices
                    </MetricTitle>
                    <MetricValue variant={'b1'} className={'active'}>
                      {RetailDeviceData?.length || 'NA'}
                    </MetricValue>
                  </MetricPaper>
                )}
              </Grid>
              <Grid item xs>
                {RetailDeviceDataPending ? (
                  <LoadingPaper>
                    <Skeleton variant="text" width={120} height={32} />
                    <Skeleton variant="rect" width={80} height={32} />
                  </LoadingPaper>
                ) : (
                  <MetricPaper>
                    <MetricTitle className={'text-color'} variant={'s1'}>
                      Inactive Devices
                    </MetricTitle>
                    <MetricValue variant={'b1'} className={'active'}>
                      {deviceNotSeenCount || 'NA'}
                    </MetricValue>
                  </MetricPaper>
                )}
              </Grid>
              <Grid item xs>
                {RetailDeviceDataPending ? (
                  <LoadingPaper>
                    <Skeleton variant="text" width={120} height={32} />
                    <Skeleton variant="rect" width={80} height={32} />
                  </LoadingPaper>
                ) : (
                  <MetricPaper>
                    <MetricTitle className={'text-color'} variant={'s1'}>
                      Itam Mismatch Devices
                    </MetricTitle>
                    <MetricValue variant={'b1'} className={'active'}>
                      {ItamMismatchCount || 'NA'}
                    </MetricValue>
                  </MetricPaper>
                )}
              </Grid>
              {/* <Grid item xs>
                {RetailDeviceDataPending ? (
                  <LoadingPaper>
                    <Skeleton variant="text" width={120} height={32} />
                    <Skeleton variant="rect" width={80} height={32} />
                  </LoadingPaper>
                ) : (
                  <MetricPaper>
                    <SGTitle className={'text-color'} variant={'s1'}>
                      Region Health
                    </SGTitle>
                    <SGBody variant={'b1'} className={'active'}>
                      {totalStores > 0 ? regionHealth + '%' : 'NA'}{' '}
                    </SGBody>
                  </MetricPaper>
                )}
              </Grid> */}
            </GridContainer>
            <HeaderSection className="universal-toggle" style={{justifyContent: 'flex-start'}}>
              <SGTitle className={'active-color'} style={{marginRight: '1rem'}}>
                View Mode:
              </SGTitle>
              <SwitchContainer>
                <SwitchLabel>Network Status</SwitchLabel>
                <Switch
                  checked={viewMode}
                  onChange={() => setViewMode(!viewMode)}
                  color="primary"
                />
                <SwitchLabel>ITAM Mismatch</SwitchLabel>
              </SwitchContainer>
            </HeaderSection>
            <GridContainer container spacing={2}>
              <Grid item xs>
                {RetailDeviceDataPending ? (
                  <SkeletonRounded variant="rect" width="100%" height={300} />
                ) : (
                  <>
                    <MapHeaderContainer>
                      <SGTitle className={'active-color'}>Store Map</SGTitle>
                    </MapHeaderContainer>
                    <StoresWorldMap
                      continent={selectedRegion}
                      storeMapData={memoizedStoreMapData}
                      viewMode={viewMode}
                    />
                  </>
                )}
              </Grid>
              <Grid item xs>
                {RetailDeviceDataPending && storeData ? (
                  <SkeletonRounded variant="rect" width="100%" height={300} />
                ) : (
                  <>
                    <SGTitle className={'active-color'}>Most Impacted Stores</SGTitle>
                    <Grid item xs={12}>
                      <Paper>
                        <Table
                          columns={tableColumns}
                          data={getMostImpactedStores()}
                          loaded={true}
                          noDataText="No store report data available"
                          disablePagination={false}
                          customRowClass={row => (row.isTotal ? 'totals-row' : '')}
                          paginationText="Stores"
                          orderByCol={'itamMismatchCount'}
                        />
                      </Paper>
                    </Grid>
                  </>
                )}
              </Grid>
            </GridContainer>
            <GridContainer container spacing={2}>
              <Grid item xs>
                <ChartContainer>
                  <ChartHeaderContainer>
                    <SGTitle className={'active-color'}>Devices Failing Test/s</SGTitle>
                  </ChartHeaderContainer>
                  {RetailDeviceDataPending && storeData ? (
                    <SkeletonRounded variant="rect" width="100%" height={400} />
                  ) : (
                    <div ref={chartContainerRef}>
                      <StyledApexLineChart>
                        <Chart
                          series={[
                            {
                              name: viewMode ? 'ITAM Mismatch' : 'Network Status',
                              data: Object.values(
                                viewMode ? deviceFailingItamData : deviceFailingPingData
                              ),
                            },
                          ]}
                          height={400}
                          type="bar"
                          options={chartOptions}
                          className={'chart'}
                        />
                      </StyledApexLineChart>
                    </div>
                  )}
                </ChartContainer>
              </Grid>
            </GridContainer>
          </div>
          <ExportModalOverlay
            open={deviceModalOpen}
            onClose={handleDeviceModalClose}
            closeAfterTransition
            BackdropComponent={Backdrop}
            BackdropProps={{timeout: 500}}
          >
            <Fade in={deviceModalOpen}>
              <DeviceModalContent>
                <DeviceModalHeader>
                  <SGHeadline className={'text-color'} variant={'h6'}>
                    {selectedChartDeviceType || 'Device Details'}
                  </SGHeadline>
                  <CloseButton aria-label="Close device details" onClick={handleDeviceModalClose}>
                    ✕
                  </CloseButton>
                </DeviceModalHeader>
                <DeviceModalDeviceList>
                  <DeviceModalMetricsRow>
                    <DeviceModalMetricCard>
                      <DeviceModalMetricLabel className={'text-color'}>
                        Total # of Devices
                      </DeviceModalMetricLabel>
                      <DeviceModalMetricValue className={'active'}>
                        {deviceModalTotalCount}
                      </DeviceModalMetricValue>
                    </DeviceModalMetricCard>
                    <DeviceModalMetricCard>
                      <DeviceModalMetricLabel className={'text-color'}>
                        # of Devices Failing Tests
                      </DeviceModalMetricLabel>
                      <DeviceModalMetricValue
                        style={{
                          color: deviceModalImpactedCount > 0 ? '#F44336' : '#4CAF50',
                        }}
                      >
                        {deviceModalImpactedCount}
                      </DeviceModalMetricValue>
                    </DeviceModalMetricCard>
                  </DeviceModalMetricsRow>
                  <Paper>
                    <Table
                      columns={(selectedChartDeviceType === 'MobilePaymentTerminal'
                        ? deviceModalColumns
                            .filter(c => c.id !== 'lastSeenDate')
                            .map(c => {
                              // Redistribute the removed Last Seen column's width (12%)
                              // to the two inventory columns so their content isn't cramped.
                              if (c.id === 'inventoryCheckStatus') return {...c, width: '20%'};
                              if (c.id === 'itamInventoryStatusDescription')
                                return {...c, width: '21%'};
                              return c;
                            })
                        : deviceModalColumns
                      ).map(c => ({...c, headerWrap: true}))}
                      data={selectedChartDevices}
                      loaded={true}
                      paginationText="Devices"
                      noDataText="No devices found for this category"
                      disablePagination={false}
                    />
                  </Paper>
                </DeviceModalDeviceList>
              </DeviceModalContent>
            </Fade>
          </ExportModalOverlay>
        </Container>
      </StyledLayout>
    </>
  );
};

export default StoreHealth;
